/*
 * Shared tooltip driver — click the "i" to toggle; the bubble's close button, Escape or
 * an outside click dismisses it.
 *
 * Hover does not open anything, so this is the only opener rather than a touch
 * enhancement over CSS: without it the "i" does nothing. It is delegated from the
 * document and holds no per-instance state, so a section re-render in the theme editor
 * needs no re-binding.
 *
 * Every consumer emits its own <script src>, and a page can hold several of them —
 * hence the flag. Two copies of these listeners would toggle a click twice and the
 * bubble would never open.
 *
 * The nudge: the bubble is anchored to its badge or bar, and one near the right edge
 * would otherwise open past the viewport. Rather than guess in CSS, measure the open
 * bubble and shift it back through a custom property the stylesheet already consumes.
 * The width is capped to the viewport first, because a bubble too wide to fit cannot be
 * nudged into view — narrow phones would only ever pick which edge it spills past.
 */
(function () {
  if (window.__themeTooltipBound) return;
  window.__themeTooltipBound = true;

  var TRIGGER = '.tooltip__trigger';
  var BUBBLE = '.tooltip__bubble';
  var CLOSE = '.tooltip__close';

  /* The page's own gutter, so a nudged bubble lands on the margin everything else uses. */
  function gutter() {
    var margin = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--page-margin'));
    return margin > 0 ? margin : 20;
  }

  function place(trigger) {
    var bubble = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!bubble) return;

    /* `clientWidth`, not `innerWidth`: the latter counts a desktop scrollbar. */
    var viewport = document.documentElement.clientWidth;
    var edge = gutter();

    bubble.style.setProperty('--tooltip-space', viewport - 2 * edge + 'px');
    bubble.style.removeProperty('--tooltip-shift');

    var rect = bubble.getBoundingClientRect();
    var shift = 0;

    if (rect.right > viewport - edge) {
      shift = viewport - edge - rect.right;
    }
    if (rect.left + shift < edge) {
      shift = edge - rect.left;
    }
    if (shift) bubble.style.setProperty('--tooltip-shift', Math.round(shift) + 'px');
  }

  function closeAll(except) {
    document.querySelectorAll(TRIGGER + '[aria-expanded="true"]').forEach(function (trigger) {
      if (trigger === except) return;
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  document.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    /* Focus goes back to the "i" the bubble belongs to — it is where the reader was,
       and after the close button disappears there is nothing else to land on. */
    var closer = target.closest(CLOSE);
    if (closer) {
      var owner = closer.closest(BUBBLE).previousElementSibling;
      if (owner && owner.matches(TRIGGER)) {
        owner.setAttribute('aria-expanded', 'false');
        owner.focus();
      }
      return;
    }

    /* A link inside an open bubble must survive the click that follows it. */
    if (target.closest(BUBBLE)) return;

    var trigger = target.closest(TRIGGER);
    if (!trigger) {
      closeAll(null);
      return;
    }

    var wasOpen = trigger.getAttribute('aria-expanded') === 'true';
    closeAll(trigger);
    trigger.setAttribute('aria-expanded', wasOpen ? 'false' : 'true');
    if (!wasOpen) place(trigger);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;

    var open = document.querySelector(TRIGGER + '[aria-expanded="true"]');
    if (!open) return;

    closeAll(null);
    open.focus();
  });

  window.addEventListener(
    'resize',
    function () {
      document.querySelectorAll(TRIGGER + '[aria-expanded="true"]').forEach(place);
    },
    { passive: true }
  );
})();
